import cx from 'clsx';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as DeleteIcon } from '@assets/svg/ic-delete-image.svg';
import { ReactComponent as TableSort } from '@assets/svg/table-sort.svg';

import TableEmptyRowExtended from '@components/common/TableEmptyRowExtended/TableEmptyRowExtended';
import TooltipSort from '@components/common/TooltipSort/TooltipSort';
import Checkbox from '@components/uikit/Checkbox/Checkbox';

import { MODAL_NAMES } from '@constants/modalRenderers';
import { initialSorting } from '@constants/tableSorting';

import { showToast } from '@helpers/showToast';

import useTranslate from '@hooks/useTranslate';
import useTranslation from '@hooks/useTranslation';

import { useModalActions } from '@store/useModalStore';

import { getEquipmentsWithTranslation } from '../../helpers/getEquipmentsWithTranslation';
import { getFilteredEquipments } from '../../helpers/getFilteredEquipments';
import type { IEquipment } from '../../model/equipmentsList ';
import { useAllEquipmentsListQuery } from '../../services/useAllEquipmentsListQuery';
import { useDeleteEquipmentTemplateMutation } from '../../services/useDeleteEquipmentTemplateMutation';
import {
  selectManageTemplateFilterLocationValue,
  selectManageTemplateFilterSearchValue,
  selectManageTemplateFilterSelectedRows,
  selectManageTemplateFilterShowIncludedTemplateValue,
  useManageTemplateFilterActions,
} from '../../store/useManageTemplateFilterStore';
import TableEquipmentsSkeleton from './TableEquipmentsSkeleton';
import style from './style.module.scss';

interface IProps {
  equipmentTableContainerRef: React.LegacyRef<HTMLDivElement>;
}

const TableEquipments: React.FC<IProps> = ({ equipmentTableContainerRef }) => {
  const { templateId } = useParams();

  const [sorting, setSorting] = useState(initialSorting);

  const { t } = useTranslation();
  const { translateField } = useTranslate();

  const { setManageTemplateFilterSelectedRows } = useManageTemplateFilterActions();

  const { setSecondaryModal } = useModalActions();

  const { mutate: deleteEquipmentTemplateMutate } = useDeleteEquipmentTemplateMutation();

  const locationValue = selectManageTemplateFilterLocationValue();
  const selectedRows = selectManageTemplateFilterSelectedRows();
  const showIncludedTemplateValue = selectManageTemplateFilterShowIncludedTemplateValue();
  const searchValue = selectManageTemplateFilterSearchValue();

  const {
    data: allEquipmentsListData,
    isLoading,
    isRefetching,
  } = useAllEquipmentsListQuery({
    locationId: locationValue.id,
  });

  const allEquipmentsWithTranslation = getEquipmentsWithTranslation(
    allEquipmentsListData || [],
    translateField,
  );

  const equipmentsData = getFilteredEquipments(allEquipmentsWithTranslation, {
    searchValue,
    orderBy: sorting.id,
    direction: sorting.desc ? 'desc' : 'asc',
    showIncludedTemplateValue,
    templateId,
  });

  const isSelectedAllLocations = locationValue.id === 'all';
  const isEquipmentsListNotEmpty = equipmentsData?.length > 0;
  const isSelectedAll = isEquipmentsListNotEmpty && equipmentsData?.length === selectedRows.size;

  const onChangeSorting = (key: string) => {
    if (!sorting.id || sorting.id !== key) {
      setSorting({ id: key, desc: false });
      return;
    }
    const { id, desc } = sorting;
    if (desc) {
      setSorting(initialSorting);
    } else {
      setSorting({ id, desc: true });
    }
  };

  const onSelectAll = () => {
    if (isSelectedAll) {
      setManageTemplateFilterSelectedRows(new Map());
    } else {
      const selectedRows = new Map(equipmentsData.map((row) => [row.id, row]));
      setManageTemplateFilterSelectedRows(selectedRows);
    }
  };

  const toggleRowSelection = (row: IEquipment) => {
    const newSelectedRows = new Map(selectedRows);
    if (newSelectedRows.has(row.id)) {
      newSelectedRows.delete(row.id);
    } else {
      newSelectedRows.set(row.id, row);
    }
    setManageTemplateFilterSelectedRows(newSelectedRows);
  };

  const deleteEquipment = (recalculate: number, equipmentId: number) => {
    deleteEquipmentTemplateMutate(
      { id: Number(templateId), equipment: [equipmentId], recalculate_cost_saving: recalculate },
      {
        onSuccess: () => {
          showToast({ variant: 'success', message: t('Your changes have been saved.') });
        },
      },
    );
  };

  const handleDeleteEquipment = (equipmentId: number) => {
    setSecondaryModal({
      name: MODAL_NAMES.modalConfirm,
      options: {
        bodyTitle: t('Recalculate archived action items?'),
        bodyContent: t(
          'After saving a new multiplication factor value, do existing completion reports (open and archive) need to be recalculated?',
        ),
        onConfirm: () => {
          deleteEquipment(1, equipmentId);
        },
        onCancel: () => {
          deleteEquipment(0, equipmentId);
        },
        confirmText: t('Recalculate'),
        cancelText: t(`Don't Recalculate`, {}, true),
      },
    });
  };

  return (
    <div className={style.equipmentTableWrapper} ref={equipmentTableContainerRef}>
      <table className={style.equipmentTable}>
        <thead>
          <tr>
            <th>
              <Checkbox
                value={isSelectedAll}
                onChange={onSelectAll}
                withHelperText={false}
                dataTooltipId="checkbox-tooltip"
                disabled={!isEquipmentsListNotEmpty}
              />
            </th>
            <th>
              {t('Location')}
              <TableSort
                className={cx(style.equipmentTableSortIcon, {
                  [style.activeSort]: sorting.id === 'location_name',
                })}
                onClick={() => onChangeSorting('location_name')}
                data-tooltip-content="location_name"
                data-tooltip-id="sort-tooltip"
              />
            </th>
            <th>
              {t('Equipment')}
              <TableSort
                className={cx(style.equipmentTableSortIcon, { [style.activeSort]: sorting.id === 'name' })}
                onClick={() => onChangeSorting('name')}
                data-tooltip-content="name"
                data-tooltip-id="sort-tooltip"
              />
            </th>
            <th>
              <div className={style.equipmentTableHeadCellContainer}>
                <span className={style.flexHeader}>{t('Asset Code')}</span>
                <TableSort
                  className={cx(style.equipmentTableSortIcon, {
                    [style.activeSort]: sorting.id === 'asset_code',
                  })}
                  onClick={() => onChangeSorting('asset_code')}
                  data-tooltip-content="asset_code"
                  data-tooltip-id="sort-tooltip"
                />
              </div>
            </th>
            <th>{t('Equipment Type')}</th>
            {showIncludedTemplateValue && <th />}
          </tr>
        </thead>
        <tbody>
          {isLoading || isRefetching ? (
            <TableEquipmentsSkeleton />
          ) : isEquipmentsListNotEmpty ? (
            equipmentsData.map((row) => (
              <tr
                key={row.id}
                className={cx(style.equipmentTableBodyRow, { [style.selectedRow]: selectedRows[row.id] })}
              >
                <td>
                  <Checkbox
                    value={selectedRows.has(row.id)}
                    onChange={() => toggleRowSelection(row)}
                    withHelperText={false}
                    dataTooltipId="checkbox-tooltip"
                  />
                </td>
                <td>
                  <div>{translateField({ item: row.locationTable })}</div>
                  {isSelectedAllLocations && (
                    <div className={cx(style.ellipsisTextOneLine, style.facilityName)}>
                      <span data-tooltip-content={row.facility_name} data-tooltip-id="cell-tooltip">
                        {row.facility_name}
                      </span>
                    </div>
                  )}
                  {row.cost_saving_template?.name && !showIncludedTemplateValue && (
                    <div className={style.equipmentAddedMessage}>
                      {t(`This equipment is already added to template {template_name}.`, {
                        template_name: row.cost_saving_template?.name,
                      })}
                    </div>
                  )}
                </td>
                <td>
                  <div className={style.ellipsisTextThreeLines}>
                    <span data-tooltip-content={translateField({ item: row })} data-tooltip-id="cell-tooltip">
                      {translateField({ item: row })}
                    </span>
                  </div>
                </td>
                <td>
                  <div className={style.ellipsisTextThreeLines}>
                    <span
                      data-tooltip-content={translateField({ item: row, field: 'asset_code' })}
                      data-tooltip-id="cell-tooltip"
                    >
                      {translateField({ item: row, field: 'asset_code' })}
                    </span>
                  </div>
                </td>
                <td>{row.type?.name || null}</td>
                {showIncludedTemplateValue && (
                  <td>
                    <div className={style.deleteIconWrapper} onClick={() => handleDeleteEquipment(row.id)}>
                      <DeleteIcon className={style.deleteIcon} />
                    </div>
                  </td>
                )}
              </tr>
            ))
          ) : (
            <TableEmptyRowExtended text={t('No equipments were found.')} />
          )}
        </tbody>
      </table>
      <TooltipSort id="sort-tooltip" sorting={sorting} />
      <Tooltip id="cell-tooltip" place="top" />
    </div>
  );
};

export default TableEquipments;
